import {getCurrentInstance} from 'vue';
import type {UnionToIntersection} from '@malt/types-utils';

// Utils types

export type ObjectEmitsOptions = Record<string, ((...args: any[]) => any) | null>;

export type EmitsOptions = ObjectEmitsOptions | string;

type EmitFn<Options = ObjectEmitsOptions, Event extends keyof Options = keyof Options> =
    Options extends Array<infer V>
        ? (event: V, ...args: any[]) => void
        : Record<string, unknown> extends Options
          ? (event: string, ...args: any[]) => void
          : UnionToIntersection<
                {
                    [key in Event]: Options[key] extends (...args: infer Args) => any
                        ? (event: key, ...args: Args) => void
                        : (event: key, ...args: any[]) => void;
                }[Event]
            >;

/**
 * Define the events of `emit` and returns the typed emit
 * ### ⚠️ Only use inside `setup` function or in a composable
 *
 * This is mainly to emit from a composable. For `<script setup/>` you can use the compiler macro `defineEmits``
 *
 * @typeParam E - The Emits options to declare
 *
 * @example
 *
 * ```ts
 *   const emit = useEmit<{
        'input': (value: string) => void;
     }>();

     emit('input', "foo"); // ✅ valid
     emit('input', 5); // ❌ invalid
     emit('foo') // ❌ invalid
 * ```
 */
export function useEmit<E extends EmitsOptions = EmitsOptions>(emitOptions?: E): EmitFn<E>;
export function useEmit<TypeEmit>(): TypeEmit;
export function useEmit() {
    const vm = getCurrentInstance();
    if (vm) {
        return vm.emit as any;
    }
    throw new Error('Only use "useEmit" inside setup function or in a composable');
}
